import { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowLeft, ArrowRight, Plus, X, Download, Save, Trash2, Eye, Undo2, Sparkles, Ruler, Palette, Gift, Search, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { imageUrl, formatApiError } from "../../lib/api";

const FONTS = ["Luckiest Guy", "Titan One"];

const SECTIONS = [
  { key: "top", label: "Üst Hediyeler", icon: ArrowUp, hint: "Yan yana dizilir" },
  { key: "left", label: "Sol Hediyeler", icon: ArrowLeft, hint: "En solda, alt alta dizilir" },
  { key: "right", label: "Sağ Hediyeler", icon: ArrowRight, hint: "En sağda, alt alta dizilir" },
];

const CHECKER = {
  backgroundImage:
    "linear-gradient(45deg,#d8d8d8 25%,transparent 25%),linear-gradient(-45deg,#d8d8d8 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d8d8d8 75%),linear-gradient(-45deg,transparent 75%,#d8d8d8 75%)",
  backgroundSize: "20px 20px",
  backgroundPosition: "0 0,0 10px,10px -10px,-10px 0",
  backgroundColor: "#ffffff",
};

function Stepper({ label, value, onChange, min = -100, max = 400, required, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">
        {label} {required && <span className="text-cyan-400">*</span>}
      </label>
      <div className="flex items-center bg-black/40 border border-white/10 rounded-xl overflow-hidden">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-minus`}
          className="px-3.5 py-2.5 text-[#64748B] hover:text-cyan-300 transition-colors text-sm">−</button>
        <input value={value} onChange={(e) => onChange(Number(e.target.value) || 0)} data-testid={testid}
          className="w-full text-center bg-transparent text-sm text-white outline-none py-2.5" />
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testid}-plus`}
          className="px-3.5 py-2.5 text-[#64748B] hover:text-cyan-300 transition-colors text-sm">+</button>
      </div>
    </div>
  );
}

let uidCounter = 1;
const newItem = (gift) => ({ uid: uidCounter++, giftId: gift.id, image: gift.image, fallback: gift.filename ? `/api/uploads/gifts/${gift.filename}` : "", text: "", textColor: "#ffffff" });

const CARD_PAD = 24;
const LINE_RATIO = 1.25;
const PREVIEW_H = 640;

export default function GiftDesigner() {
  const [mode, setMode] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [gifts, setGifts] = useState([]);

  const [name, setName] = useState("");
  const [font, setFont] = useState("Luckiest Guy");
  const [giftSize, setGiftSize] = useState(70);
  const [giftGap, setGiftGap] = useState(0);
  const [textGap, setTextGap] = useState(-10);
  const [lineGap, setLineGap] = useState(-5);
  const [textWrap, setTextWrap] = useState(220);
  const [fontSize, setFontSize] = useState(24);
  const [borderColor, setBorderColor] = useState("#000000");
  const [borderWidth, setBorderWidth] = useState(3);
  const [blur, setBlur] = useState(0);
  const [grayscale, setGrayscale] = useState(false);
  const [sections, setSections] = useState({ top: [], left: [], right: [] });
  const [pickerSection, setPickerSection] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [dragItem, setDragItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [canvasDims, setCanvasDims] = useState({ w: 1920, h: 240 });

  const canvasRef = useRef(null);
  const imgCache = useRef({});

  const fetchDesigns = () => api.get("/member/designs").then((r) => setDesigns(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/member/gifts").then((r) => setGifts(r.data)).catch(() => {});
    fetchDesigns();
  }, []);

  const addItem = (section, gift) => setSections((p) => ({ ...p, [section]: [...p[section], newItem(gift)] }));
  const removeItem = (section, uid) => setSections((p) => ({ ...p, [section]: p[section].filter((i) => i.uid !== uid) }));
  const updateItem = (section, uid, patch) =>
    setSections((p) => ({ ...p, [section]: p[section].map((i) => (i.uid === uid ? { ...i, ...patch } : i)) }));
  const onDropItem = (section, targetIndex) => {
    if (!dragItem || dragItem.section !== section || dragItem.index === targetIndex) {
      setDragItem(null);
      return;
    }
    setSections((p) => {
      const arr = [...p[section]];
      const [moved] = arr.splice(dragItem.index, 1);
      arr.splice(targetIndex, 0, moved);
      return { ...p, [section]: arr };
    });
    setDragItem(null);
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      if (imgCache.current[src]) return resolve(imgCache.current[src]);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => { imgCache.current[src] = img; resolve(img); };
      img.onerror = reject;
      img.src = src;
    });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;

    const draw = async () => {
      await Promise.all(FONTS.map((f) => document.fonts.load(`24px "${f}"`).catch(() => {})));
      if (cancelled) return;
      const ctx = canvas.getContext("2d");
      const pad = 64;

      const wrapLines = (text) => {
        const raw = (text || "").trim();
        if (!raw) return [];
        ctx.font = `${fontSize}px "${font}"`;
        const out = [];
        for (const hard of raw.split("\n")) {
          const words = hard.split(/\s+/).filter(Boolean);
          if (!words.length) continue;
          let cur = words[0];
          for (let i = 1; i < words.length; i++) {
            const trial = cur + " " + words[i];
            if (ctx.measureText(trial).width <= textWrap) cur = trial;
            else { out.push(cur); cur = words[i]; }
          }
          out.push(cur);
        }
        return out;
      };
      const met = new Map();
      const mOf = (it) => {
        if (!met.has(it.uid)) {
          const ls = wrapLines(it.text);
          let tw = 0;
          for (const l of ls) tw = Math.max(tw, ctx.measureText(l).width);
          const textH = ls.length ? textGap + ls.length * fontSize * LINE_RATIO + 6 : 0;
          met.set(it.uid, { lines: ls, cardW: Math.max(giftSize, tw + CARD_PAD * 2), cardH: giftSize + Math.max(0, textH) });
        }
        return met.get(it.uid);
      };
      const topCardW1 = sections.top.length ? Math.max(...sections.top.map((it) => mOf(it).cardW)) : 0;
      const topH1 = sections.top.length ? Math.max(...sections.top.map((it) => mOf(it).cardH)) : 0;
      const colH1 = (arr) => (arr.length ? arr.reduce((a, it) => a + mOf(it).cardH, 0) + (arr.length - 1) * giftGap : 0);
      const midH1 = Math.max(colH1(sections.left), colH1(sections.right));
      const gapTop1 = sections.top.length && midH1 > 0 ? lineGap : 0;
      const leftW1 = sections.left.length ? Math.max(...sections.left.map((it) => mOf(it).cardW)) : 0;
      const rightW1 = sections.right.length ? Math.max(...sections.right.map((it) => mOf(it).cardW)) : 0;
      const sharedW = Math.max(topCardW1, leftW1);
      const topNatW = sections.top.length ? sections.top.length * sharedW + (sections.top.length - 1) * giftGap : 0;
      const middleMin = giftSize + giftGap * 2;
      const CW = Math.max(1920, Math.ceil(pad * 2 + Math.max(topNatW, sharedW + middleMin + rightW1)));
      const topH = topH1;
      const gapAfterTop = gapTop1;
      const CH = Math.max(240, Math.ceil(pad * 2 + topH + gapAfterTop + midH1));
      const topCardW = sharedW;
      const topGap = giftGap;
      const colGap = giftGap;

      if (canvas.width !== CW) canvas.width = CW;
      if (canvas.height !== CH) canvas.height = CH;
      ctx.clearRect(0, 0, CW, CH);
      setCanvasDims((p) => (p.w === CW && p.h === CH ? p : { w: CW, h: CH }));

      const drawText = (lines, color, cx, y, fs, bw, blurE) => {
        if (!lines.length) return;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.lineJoin = "round";
        ctx.shadowColor = borderColor;
        ctx.shadowBlur = Math.max(0, blurE);
        let yy = y;
        for (const line of lines) {
          let lineFs = fs;
          ctx.font = `${lineFs}px "${font}"`;
          let w = ctx.measureText(line).width;
          const maxW = CW - pad * 2;
          if (w > maxW && w > 0) {
            lineFs = (fs * maxW) / w;
            ctx.font = `${lineFs}px "${font}"`;
            w = maxW;
          }
          const safeCx = Math.min(Math.max(cx, w / 2 + 4), CW - w / 2 - 4);
          if (bw > 0) {
            ctx.lineWidth = bw * 2;
            ctx.strokeStyle = borderColor;
            ctx.strokeText(line, safeCx, yy);
          }
          ctx.fillStyle = color;
          ctx.fillText(line, safeCx, yy);
          yy += lineFs * LINE_RATIO;
        }
      };

      ctx.save();
      if (grayscale) ctx.filter = "grayscale(1)";
      const jobs = [];
      const oy = pad;
      sections.top.forEach((it, i) => jobs.push({ it, x: pad + i * (topCardW + topGap), y: oy, w: topCardW, k: 1 }));
      const midY = oy + topH + gapAfterTop;
      let curY = midY;
      sections.left.forEach((it) => { jobs.push({ it, x: pad, y: curY, w: sharedW, k: 1 }); curY += mOf(it).cardH + colGap; });
      curY = midY;
      sections.right.forEach((it) => { jobs.push({ it, x: CW - pad - rightW1, y: curY, w: rightW1, k: 1 }); curY += mOf(it).cardH + colGap; });

      for (const job of jobs) {
        try {
          const img = await loadImage(imageUrl(job.it.image));
          if (cancelled) return;
          const iS = giftSize * job.k;
          ctx.drawImage(img, job.x + (job.w - iS) / 2, job.y, iS, iS);
          job.textY = job.y + iS + textGap * job.k;
        } catch (e) { /* görsel yüklenemedi */ }
      }
      ctx.restore();

      for (const job of jobs) {
        if (job.textY !== undefined)
          drawText(mOf(job.it).lines, job.it.textColor, job.x + job.w / 2, job.textY, fontSize * job.k, borderWidth * job.k, blur * job.k);
      }
    };
    draw();
    return () => { cancelled = true; };
  }, [sections, font, giftSize, giftGap, textGap, lineGap, fontSize, textWrap, borderColor, borderWidth, blur, grayscale]);

  const downloadPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(name || "hediye-tasarimi").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PNG indirildi (transparan arka plan)");
    }, "image/png");
  };

  const buildConfig = () => ({
    font, giftSize, giftGap, textGap, lineGap, textWrap, fontSize, borderColor, borderWidth, blur, grayscale,
    sections: Object.fromEntries(
      Object.entries(sections).map(([k, arr]) => [k, arr.map(({ giftId, text, textColor }) => ({ giftId, text, textColor }))])
    ),
  });

  const save = async () => {
    if (saving) return;
    if (!name.trim()) {
      toast.error("Kaydetmek için önce tasarım adı girmelisiniz");
      return;
    }
    setSaving(true);
    try {
      if (editingId) await api.delete(`/member/designs/${editingId}`);
      await api.post("/member/designs", { name: name.trim(), config: buildConfig() });
      toast.success("Tasarım kaydedildi");
      await fetchDesigns();
      setMode("list");
      setEditingId(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => {
    setEditingId(null);
    setName("");
    setSections({ top: [], left: [], right: [] });
    setMode("edit");
  };

  const loadDesign = async (d) => {
    let gList = gifts;
    if (!gList.length) {
      try {
        const r = await api.get("/member/gifts");
        gList = r.data;
        setGifts(gList);
      } catch (e) {
        toast.error("Hediyeler yüklenemedi, tekrar deneyin");
        return;
      }
    }
    const c = d.config || {};
    setName(d.name);
    setFont(c.font || "Luckiest Guy");
    setGiftSize(c.giftSize ?? 70);
    setGiftGap(c.giftGap ?? 0);
    setTextGap(c.textGap ?? -10);
    setLineGap(c.lineGap ?? -5);
    setTextWrap(c.textWrap ?? 220);
    setFontSize(c.fontSize ?? 24);
    setBorderColor(c.borderColor || "#000000");
    setBorderWidth(c.borderWidth ?? 3);
    setBlur(c.blur ?? 0);
    setGrayscale(!!c.grayscale);
    const findGift = (id) => gList.find((g) => g.id === id);
    const hydrate = (arr) =>
      (arr || []).filter((i) => findGift(i.giftId)).map((i) => ({ uid: uidCounter++, giftId: i.giftId, image: findGift(i.giftId).image, fallback: findGift(i.giftId).filename ? `/api/uploads/gifts/${findGift(i.giftId).filename}` : "", text: i.text || "", textColor: i.textColor || "#ffffff" }));
    setSections({
      top: hydrate(c.sections?.top),
      left: hydrate(c.sections?.left),
      right: hydrate(c.sections?.right),
    });
    setEditingId(d.id);
    setMode("edit");
  };

  const deleteDesign = async (id) => {
    try {
      await api.delete(`/member/designs/${id}`);
      setDesigns((prev) => prev.filter((d) => d.id !== id));
      toast.success("Tasarım silindi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";
  const sectionTitle = "text-[11px] font-bold tracking-wide text-cyan-300 mb-3 flex items-center gap-1.5";

  if (mode === "list") {
    return (
      <div data-testid="designer-list">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-700 text-lg text-white">Kaydedilen Tasarımlar ({designs.length})</h2>
          <button onClick={startNew} data-testid="designer-create-button"
            className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full">
            <Plus size={15} /> Görsel Oluştur
          </button>
        </div>
        {designs.length === 0 ? (
          <div data-testid="designer-list-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0E17]/60 py-20 text-center">
            <Gift size={32} strokeWidth={1.5} className="text-[#64748B] mx-auto mb-4" />
            <p className="text-[#A0ABC0] text-sm mb-6">Henüz kaydedilmiş tasarım yok.</p>
            <button onClick={startNew} data-testid="designer-create-empty-button"
              className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full">
              <Plus size={15} /> İlk Tasarımı Oluştur
            </button>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="designer-saved-list">
            {designs.map((d) => (
              <div key={d.id} data-testid={`designer-saved-${d.id}`}
                className="flex items-center gap-3 bg-[#0A0E17] border border-white/[0.07] rounded-xl px-4 py-3.5">
                <Gift size={16} strokeWidth={1.5} className="text-cyan-300 shrink-0" />
                <span className="text-sm font-semibold text-white flex-1 truncate">{d.name}</span>
                <span className="text-[11px] text-[#64748B] shrink-0">{d.created_at ? new Date(d.created_at).toLocaleDateString("tr-TR") : ""}</span>
                <button onClick={() => loadDesign(d)} data-testid={`designer-load-${d.id}`}
                  className="text-[11px] font-semibold text-cyan-300 border border-cyan-400/30 rounded-full px-3.5 py-1.5 hover:bg-cyan-400/10 transition-colors">
                  Düzenle
                </button>
                <button onClick={() => deleteDesign(d.id)} data-testid={`designer-delete-${d.id}`} aria-label="Sil"
                  className="w-7 h-7 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/40 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="gift-designer">
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <div className="space-y-4">
          {SECTIONS.map((s) => (
            <div key={s.key} className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-4" data-testid={`section-${s.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <s.icon size={14} strokeWidth={1.5} className="text-cyan-300" />
                <span className="text-xs font-semibold text-white">{s.label}</span>
                <span className="text-[10px] text-[#64748B] ml-auto">{s.hint}</span>
              </div>
              <div className="space-y-2 mb-3">
                {sections[s.key].length === 0 && (
                  <div className="rounded-xl border border-dashed border-white/10 py-4 text-center text-[11px] text-[#64748B]">
                    Hediye eklenmedi
                  </div>
                )}
                {sections[s.key].map((it, idx) => (
                  <div key={it.uid} data-testid={`section-item-${s.key}-${it.uid}`}
                    draggable
                    onDragStart={() => setDragItem({ section: s.key, index: idx })}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropItem(s.key, idx)}
                    onDragEnd={() => setDragItem(null)}
                    className={`flex items-center gap-2 bg-black/30 border rounded-xl p-2 cursor-grab active:cursor-grabbing transition-colors ${
                      dragItem?.section === s.key && dragItem?.index === idx ? "opacity-40 border-cyan-400/50" : "border-white/[0.06] hover:border-cyan-400/30"
                    }`}>
                    <GripVertical size={14} className="text-[#64748B] shrink-0" data-testid={`item-drag-${s.key}-${it.uid}`} />
                    <div className="w-9 h-9 rounded-lg bg-[#0F1626] flex items-center justify-center shrink-0">
                      <img src={imageUrl(it.image)} alt="" className="max-w-full max-h-full object-contain" />
                    </div>
                    <textarea
                      value={it.text}
                      onChange={(e) => updateItem(s.key, it.uid, { text: e.target.value })}
                      placeholder="Hediye yazısı... (Enter = alt satır)"
                      rows={1}
                      data-testid={`item-text-${s.key}-${it.uid}`}
                      className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-2.5 py-2 text-xs text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 resize-none min-h-[36px]"
                    />
                    <input
                      type="color"
                      value={it.textColor}
                      onChange={(e) => updateItem(s.key, it.uid, { textColor: e.target.value })}
                      title="Yazı rengi"
                      data-testid={`item-color-${s.key}-${it.uid}`}
                      className="w-8 h-8 rounded-lg cursor-pointer bg-black/40 border border-white/10 p-0.5 shrink-0"
                    />
                    <button onClick={() => removeItem(s.key, it.uid)} data-testid={`item-delete-${s.key}-${it.uid}`} aria-label="Kaldır"
                      className="text-[#64748B] hover:text-red-300 transition-colors shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={() => { setPickerSection(s.key); setPickerSearch(""); }} data-testid={`section-add-${s.key}`}
                className="btn-glow w-full inline-flex items-center justify-center gap-2 text-xs font-semibold text-white py-2.5 rounded-full">
                <Plus size={14} /> Yeni Hediye Ekle
              </button>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-6">
            <h3 className={sectionTitle}><Sparkles size={13} /> TEMEL</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Tasarım Adı <span className="text-cyan-400">*</span></label>
                <input data-testid="designer-name-input" className={inputCls} placeholder="Örn: Minecraft Hediyeleri" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Font <span className="text-cyan-400">*</span></label>
                <select data-testid="designer-font-select" className={`${inputCls} cursor-pointer`} value={font} onChange={(e) => setFont(e.target.value)}>
                  {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-6">
            <h3 className={sectionTitle}><Ruler size={13} /> BOYUTLAR</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <Stepper label="Hediye Boyutu" required value={giftSize} onChange={setGiftSize} min={20} max={300} testid="designer-gift-size" />
              <Stepper label="Hediye Mesafesi" required value={giftGap} onChange={setGiftGap} min={0} max={200} testid="designer-gift-gap" />
              <Stepper label="Yazı Mesafesi" required value={textGap} onChange={setTextGap} min={-100} max={200} testid="designer-text-gap" />
              <Stepper label="Satır Arası" required value={lineGap} onChange={setLineGap} min={-50} max={100} testid="designer-line-gap" />
              <Stepper label="Metin Sarma" required value={textWrap} onChange={setTextWrap} min={60} max={600} testid="designer-text-wrap" />
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-6">
            <h3 className={sectionTitle}><Palette size={13} /> STİL</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 items-end">
              <Stepper label="Yazı Boyutu" required value={fontSize} onChange={setFontSize} min={8} max={120} testid="designer-font-size" />
              <div>
                <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Border Rengi <span className="text-cyan-400">*</span></label>
                <input type="color" value={borderColor} onChange={(e) => setBorderColor(e.target.value)} data-testid="designer-border-color"
                  className="w-full h-[42px] bg-black/40 border border-white/10 rounded-xl cursor-pointer p-1" />
              </div>
              <Stepper label="Border Kalınlığı" required value={borderWidth} onChange={setBorderWidth} min={0} max={30} testid="designer-border-width" />
              <Stepper label="Oto Blur" value={blur} onChange={setBlur} min={0} max={50} testid="designer-blur" />
              <div>
                <label className="block text-[11px] font-semibold text-[#A0ABC0] mb-1.5">Gri Hediye</label>
                <button onClick={() => setGrayscale(!grayscale)} data-testid="designer-grayscale-toggle" aria-label="Gri hediye"
                  className={`w-11 h-[24px] rounded-full transition-colors relative ${grayscale ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF]" : "bg-white/10"}`}>
                  <span className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-all ${grayscale ? "left-[24px]" : "left-[3px]"}`} />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={downloadPng} data-testid="designer-download-button"
                className="inline-flex items-center gap-2 text-xs font-semibold text-white border border-white/15 bg-black/40 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors rounded-full px-5 py-2.5">
                <Download size={14} /> PNG İndir
              </button>
              <button onClick={save} disabled={saving} data-testid="designer-save-button"
                className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full disabled:opacity-50">
                <Save size={14} /> {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>

        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-6 mt-6" data-testid="designer-preview-card">
        <h3 className={sectionTitle}><Eye size={13} /> Canlı Önizleme</h3>
        <div className="rounded-xl overflow-y-auto overflow-x-hidden p-6 min-h-[220px] max-h-[70vh] flex justify-center" style={CHECKER} data-testid="designer-preview">
          <canvas ref={canvasRef} data-testid="designer-canvas" className="h-auto max-w-full"
            style={{ width: canvasDims.h > PREVIEW_H ? `${(PREVIEW_H / canvasDims.h) * canvasDims.w}px` : "100%" }} />
        </div>
        <p className="text-center text-[11px] text-[#64748B] mt-4">Çıktı: en az 1920px genişlik · Üst sıra kalabalıklaşınca çerçeve yanlara genişler · Sol/sağ hediyeler kenarlara tam oturur</p>
      </div>

      <div className="mt-6">
        <button onClick={() => setMode("list")} data-testid="designer-back-button"
          className="inline-flex items-center gap-2 text-xs font-semibold text-[#A0ABC0] hover:text-cyan-200 transition-colors">
          <Undo2 size={14} /> Geri Dön
        </button>
      </div>

      <Dialog open={!!pickerSection} onOpenChange={(o) => !o && setPickerSection(null)}>
        <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto" data-testid="designer-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">Hediye Seç</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Tasarımına eklemek için bir hediye seç.</DialogDescription>
          </DialogHeader>
          <div className="relative mb-4">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Hediye ara (Türkçe veya İngilizce)..."
              data-testid="designer-picker-search"
              className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60"
            />
          </div>
          {gifts.length === 0 ? (
            <p className="text-sm text-[#64748B] py-8 text-center">Henüz onaylanmış görsel yok. Admin onayından sonra burada listelenecek.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5" data-testid="designer-picker-grid">
              {gifts
                .filter((g) => {
                  const q = pickerSearch.toLowerCase();
                  return (g.name || "").toLowerCase().includes(q) || (g.name_tr || "").toLowerCase().includes(q);
                })
                .map((g) => (
                  <button key={g.id} onClick={() => { addItem(pickerSection, g); setPickerSection(null); }} data-testid={`designer-pick-${g.id}`}
                    className="rounded-xl bg-[#0F1626] border border-white/[0.07] hover:border-cyan-400/50 transition-colors p-2 flex flex-col items-center gap-1.5">
                    <div className="w-12 h-12 flex items-center justify-center">
                      <img src={imageUrl(g.image)} alt={g.name || "Hediye"} className="max-w-full max-h-full object-contain" loading="lazy" />
                    </div>
                    <span className="text-[10px] font-semibold text-white truncate w-full text-center">{g.name || "Hediye"}</span>
                    {g.name_tr && <span className="text-[9px] text-[#64748B] truncate w-full text-center -mt-1" data-testid={`designer-pick-name-tr-${g.id}`}>{g.name_tr}</span>}
                    <span className="text-[10px] font-bold text-cyan-300 bg-cyan-400/10 border border-cyan-400/25 rounded-full px-2 py-0.5" data-testid={`designer-pick-price-${g.id}`}>
                      {g.price ?? 0} Jeton
                    </span>
                  </button>
                ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
